import { Component } from "@theme/component";
import { ThemeEvents } from "@theme/events";
import { morph } from "@theme/morph";
import { debounce, onAnimationEnd, readDurationToken, yieldToMainThread } from "@theme/utilities";

const STICKY_ADD_TO_CART = "sticky-add-to-cart";
const SHOPIFY_SECTION_SELECTOR = ".shopify-section";
const TRUE = "true";
const FALSE = "false";
const ADDED_DWELL_FALLBACK_MS = 800;
// Longest checkmark exit is 210ms (70ms delay + 140ms fade); the small buffer
// keeps the control locked through the final painted frame.
const ADDED_EXIT_GUARD_MS = 240;
const HIDDEN_CLASS = "hidden";
const ADD_TO_CART_ERROR_REF = "addToCartTextError";
/** How far (px) the footer's first visible content stays below the viewport bottom when the bar hides — small, so the bar tucks away just *before* footer content appears (over the footer's empty top padding, not over the content). */
const FOOTER_HIDE_LEAD_PX = 8;
/** Min viewport width (px) treated as desktop/tablet; mirrors the 749px mobile CSS breakpoint. */
const DESKTOP_MIN_WIDTH = 750;
/** Minimum visible-scroll window (px) before the bar may show. Below it the bar never appears, so it can't flash in-and-out on short pages. Split by device: desktop pages are shorter and scroll slower (less flash-prone) than mobile flicks. */
const MIN_VISIBLE_SCROLL_DESKTOP_PX = 200;
const MIN_VISIBLE_SCROLL_MOBILE_PX = 250;
/** A hard-navigation can upgrade this element before the product form/footer is parsed. */
const SETUP_RETRY_LIMIT = 20;
const SETUP_RETRY_DELAY_MS = 50;
const VIRTUAL_KEYBOARD_MIN_DELTA_PX = 120;
const STICKY_BINDING_SELECTOR =
  'product-form-component, .buy-buttons-block, [ref="addToCartButton"], [ref="addToCartTextError"]';
/** The page's own footer, as for the contentinfo landmark: not a footer inside the content, such as a review card's. */
const PAGE_FOOTER_SELECTOR = "footer:not(main footer, article footer, aside footer, nav footer, section footer)";
const FOOTER_BINDING_SELECTOR = `${PAGE_FOOTER_SELECTOR}, [class*="footer-group"]`;
const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {HTMLElement & {
 *   requestAddToCart: (initiator: HTMLButtonElement, event?: Event) => Promise<void>
 * }} ProductFormElement
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 * @property {HTMLElement} [stickyError] - Error banner mirrored from the product form
 * @property {HTMLElement} [stickyErrorText] - Text node container for the mirrored error
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ["stickyBar", "addToCartButton", "quantityDisplay", "quantityNumber"];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {MutationObserver | null} */
  #sectionMutationObserver = null;

  /** @type {MutationObserver | null} */
  #footerMutationObserver = null;

  /** @type {MutationObserver | null} */
  #lateFooterMutationObserver = null;

  /** @type {number | undefined} */
  #setupRetryTimeout;

  /** @type {number} */
  #setupRetryCount = 0;

  /** @type {boolean} */
  #setupQueued = false;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {HTMLButtonElement | null} */
  #boundStickyButton = null;

  /** @type {HTMLElement | null} */
  #lastFocusedStickyControl = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  /**
   * The footer the bar hides at as it scrolls into view.
   * @type {HTMLElement | null}
   */
  #footer = null;

  /**
   * The buy-buttons block whose visibility drives showing/hiding the bar.
   * @type {Element | null}
   */
  #buyButtonsBlock = null;

  /**
   * Whether the buy buttons are scrolled ABOVE the viewport (the popup's active zone).
   * Drives source-error suppression — a STABLE signal that only flips when you cross
   * the buy buttons, unlike the bar's stuck state which oscillates at the footer.
   * @type {boolean}
   */
  #buyButtonsAbove = false;

  /**
   * The footer's inner content wrapper. Its top padding is the gap between the footer
   * box top and its first visible content (the newsletter / link columns); the hide
   * trigger subtracts it so the bar tucks away just before that content appears.
   * @type {HTMLElement | null}
   */
  #footerContentEl = null;

  /**
   * The footer observer's bottom rootMargin magnitude (px) = the footer content's top
   * padding − FOOTER_HIDE_LEAD_PX. Re-measured on setup and resize.
   * @type {number}
   */
  #footerHideMargin = 0;

  /**
   * The product form's add-to-cart error element being mirrored into the bar.
   * @type {HTMLElement | null}
   */
  #sourceErrorElement = null;

  /**
   * Watches {@link #sourceErrorElement} for show/hide and text changes.
   * @type {MutationObserver | null}
   */
  #errorObserver = null;

  /** @type {boolean} */
  #addToCartTransactionActive = false;

  /** @type {CustomEvent | null} */
  #pendingVariantUpdate = null;

  /**
   * Handles the add to cart button click in the sticky bar
   * @param {Event} event
   */
  handleAddToCartClick = event => {
    const { addToCartButton, stickyBar } = this.refs;
    if (stickyBar.inert || this.dataset.variantAvailable === FALSE || addToCartButton.disabled) {
      event.preventDefault();
      return;
    }

    const productForm = this.#getProductForm();
    if (!productForm || !("requestAddToCart" in productForm)) {
      return;
    }

    productForm.requestAddToCart(addToCartButton, event);
  };

  /**
   * Runs sticky success motion only after the shared form transaction succeeds.
   * @param {{flyToCart?: boolean}} [options]
   */
  animateAddToCartSuccess = async ({ flyToCart = false } = {}) => {
    const { addToCartButton } = this.refs;

    if (addToCartButton.dataset.added !== TRUE) {
      addToCartButton.dataset.added = TRUE;
    }

    const animatedElements = /** @type {Element[]} */ ([addToCartButton]);
    const cartIcon = flyToCart ? document.querySelector(".header-actions__cart-icon") : null;

    if (cartIcon && this.refs.productImage) {
      const flyToCartElement = /** @type {FlyToCart} */ (document.createElement("fly-to-cart"));

      flyToCartElement.classList.add("fly-to-cart--sticky");
      flyToCartElement.style.setProperty("background-image", `url(${this.refs.productImage.src})`);
      flyToCartElement.useSourceSize = TRUE;
      flyToCartElement.source = this.refs.productImage;
      flyToCartElement.destination = cartIcon;

      document.body.append(flyToCartElement);
      animatedElements.push(flyToCartElement);
    }

    await onAnimationEnd(animatedElements);

    // Match the primary button's complete entrance, dwell, and exit lock so the
    // synchronized controls cannot become clickable while either checkmark remains.
    await new Promise(resolve =>
      setTimeout(resolve, readDurationToken("--add-to-cart-dwell-duration", ADDED_DWELL_FALLBACK_MS, this)),
    );

    addToCartButton.dataset.addedExiting = TRUE;
    delete addToCartButton.dataset.added;
    await yieldToMainThread();
    await Promise.all([
      onAnimationEnd(addToCartButton),
      new Promise(resolve => setTimeout(resolve, ADDED_EXIT_GUARD_MS)),
    ]);
    delete addToCartButton.dataset.addedExiting;
  };

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = event => {
    if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    if (this.#addToCartTransactionActive) {
      this.#pendingVariantUpdate = event;
      return;
    }

    this.#applyVariantUpdate(event);
  };

  /** @param {CustomEvent} event */
  #applyVariantUpdate = event => {
    const variant = event.detail.resource;

    // Get the new sticky add to cart HTML from the server response
    const newStickyAddToCart = event.detail.data.html.querySelector(STICKY_ADD_TO_CART);
    if (!newStickyAddToCart) {
      return;
    }

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) {
      return;
    }

    // Store current visibility state before morphing
    const currentStuck = this.refs.stickyBar.dataset.stuck || FALSE;
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;
    const activeElement = document.activeElement;
    if (activeElement instanceof HTMLElement && this.refs.stickyBar.contains(activeElement)) {
      this.#lastFocusedStickyControl = activeElement;
    }

    // Morph the entire sticky bar content
    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    // Restore visibility state after morphing
    this.refs.stickyBar.dataset.stuck = currentStuck;
    this.dataset.variantAvailable = variantAvailable ?? FALSE;

    // Update the dataset attributes with new variant info
    if (variant?.id) {
      this.dataset.currentVariantId = variant.id;
    }

    // `childrenOnly` morphing does not call Component.updatedCallback for this host.
    // Re-sync refs now, then let the same idempotent setup path rebind any replaced
    // sticky/primary controls and source-error node.
    this.updatedCallback();
    this.#setupBindings();

    if (variant === null) {
      this.#handleVariantUnavailable();
    }
    // Restore the current quantity display if needed
    this.#updateButtonText();
    this.#reconcileVisibilityFromGeometry();
  };

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = event => {
    // The variant update event will follow and handle all updates via morph
    // We just update the dataset here for tracking
    const variantId = event.detail.resource?.id;
    if (!variantId) {
      return;
    }
    this.dataset.currentVariantId = variantId;
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = "";
    const variantTitleElement = this.querySelector(".sticky-add-to-cart__variant");
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) {
      return;
    }

    const selectedOptions = [...variantPicker.querySelectorAll("input:checked")]
      .map(option => /** @type {HTMLInputElement} */ (option).value)
      .filter(value => value !== "")
      .join(" / ");
    if (!selectedOptions) {
      return;
    }
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles quantity selector update events
   * @param {import("@theme/events").QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = event => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) {
      return;
    }

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Uses only the current observed buy-buttons node; callbacks retained by a browser
   * after a section replacement cannot mutate the new binding.
   * @param {IntersectionObserverEntry[]} entries
   */
  #onBuyButtonsIntersect = entries => {
    const entry = entries.find(candidate => candidate.target === this.#buyButtonsBlock);
    if (!entry) {
      return;
    }

    const rect = entry.boundingClientRect;
    this.#buyButtonsAbove = !entry.isIntersecting && (rect.bottom <= 0 || rect.top < 0);
    this.#syncFooterStateFromGeometry();
    this.#updateStickyVisibility();
  };

  /**
   * Handles a footer-observer intersection: hides the bar as the footer reaches it,
   * and shows it again on scroll-up while the buy buttons are still above the viewport.
   * @param {IntersectionObserverEntry[]} entries
   */
  #onFooterIntersect = entries => {
    const entry = entries.find(candidate => candidate.target === this.#footer);
    if (!entry) {
      return;
    }

    this.#hiddenByBottom = entry.isIntersecting;
    this.#syncBuyButtonsStateFromGeometry();
    this.#updateStickyVisibility();
  };

  /**
   * Resize, rotation, and the visual viewport changing for a virtual keyboard all use
   * the same rebinding/reconciliation path. Debouncing prevents layout-read bursts.
   */
  #handleViewportChange = debounce(() => {
    if (!this.isConnected) {
      return;
    }

    this.#queueSetup({ resetRetry: true });
  }, 100);

  /** Revalidate bindings after bfcache/history restoration. */
  #handleHistoryRestore = () => {
    this.#queueSetup({ resetRetry: true });
  };

  /** @param {FocusEvent} event */
  #handleDocumentFocusIn = event => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) {
      return;
    }

    this.#lastFocusedStickyControl = this.refs.stickyBar.contains(target) ? target : null;
  };

  connectedCallback() {
    super.connectedCallback();

    // Recreate per connect: disconnect aborts every listener registered below.
    this.#abortController.abort();
    this.#abortController = new AbortController();
    this.#setupRetryCount = 0;
    this.#setupQueued = false;

    const { signal } = this.#abortController;
    const target = this.closest(SHOPIFY_SECTION_SELECTOR);
    const onVariantUpdate = /** @type {EventListener} */ (/** @type {unknown} */ (this.#handleVariantUpdate));
    const onVariantSelected = /** @type {EventListener} */ (/** @type {unknown} */ (this.#handleVariantSelected));
    const onQuantityUpdate = /** @type {EventListener} */ (/** @type {unknown} */ (this.#handleQuantityUpdate));

    target?.addEventListener(ThemeEvents.variantUpdate, onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, onVariantSelected, { signal });

    document.addEventListener(ThemeEvents.quantitySelectorUpdate, onQuantityUpdate, { signal });
    document.addEventListener("focusin", this.#handleDocumentFocusIn, { capture: true, signal });
    globalThis.addEventListener("resize", this.#handleViewportChange, { passive: true, signal });
    globalThis.addEventListener("orientationchange", this.#handleViewportChange, { passive: true, signal });
    globalThis.addEventListener("pageshow", this.#handleHistoryRestore, { signal });
    globalThis.addEventListener("popstate", this.#handleHistoryRestore, { signal });
    globalThis.visualViewport?.addEventListener("resize", this.#handleViewportChange, { passive: true, signal });

    this.#getInitialQuantity();
    this.#observeSectionChanges();
    this.#setupBindings();
  }

  updatedCallback() {
    super.updatedCallback();

    if (!this.isConnected) {
      return;
    }

    this.#updateButtonText();
    this.#queueSetup({ resetRetry: true });
  }

  disconnectedCallback() {
    this.#recoverFocusForDetachedStickyControl();
    this.#hideStickyBar();
    // Restore the original error element in case it was suppressed while mirrored.
    this.#setSourceErrorSuppressed(false);
    this.#clearSetupRetry(true);
    this.#handleViewportChange.cancel();
    this.#sectionMutationObserver?.disconnect();
    this.#sectionMutationObserver = null;
    this.#disconnectLateFooterObserver();
    this.#disconnectLayoutObservers();
    this.#errorObserver?.disconnect();
    this.#errorObserver = null;
    this.#sourceErrorElement = null;
    this.#abortController.abort();
    this.#boundStickyButton = null;
    this.#lastFocusedStickyControl = null;
    super.disconnectedCallback();
  }

  /**
   * Coalesces section morph/update hooks into one setup pass.
   * @param {{resetRetry?: boolean}} [options]
   */
  #queueSetup({ resetRetry = false } = {}) {
    if (resetRetry) {
      this.#setupRetryCount = 0;
    }
    if (this.#setupQueued) {
      return;
    }

    this.#setupQueued = true;
    queueMicrotask(() => {
      this.#setupQueued = false;
      if (this.isConnected) {
        this.#setupBindings();
      }
    });
  }

  /** Schedules a finite parse-order retry when required external nodes are late. */
  #scheduleSetupRetry() {
    if (this.#setupRetryTimeout || this.#setupRetryCount >= SETUP_RETRY_LIMIT) {
      return;
    }

    this.#setupRetryCount += 1;
    this.#setupRetryTimeout = globalThis.setTimeout(() => {
      this.#setupRetryTimeout = undefined;
      if (this.isConnected) {
        this.#setupBindings();
      }
    }, SETUP_RETRY_DELAY_MS);
  }

  /** @param {boolean} resetCount */
  #clearSetupRetry(resetCount = false) {
    if (this.#setupRetryTimeout) {
      clearTimeout(this.#setupRetryTimeout);
      this.#setupRetryTimeout = undefined;
    }
    if (resetCount) {
      this.#setupRetryCount = 0;
    }
  }

  /**
   * Idempotently resolves and binds the current form, buy-buttons block, primary
   * button, footer, and sticky button. Missing nodes keep the bar inert and retry
   * only within the finite setup window.
   * @returns {boolean}
   */
  #setupBindings() {
    this.#clearSetupRetry();

    const productForm = this.#getProductForm();
    const buyButtonsBlock = productForm?.closest(".buy-buttons-block") ?? null;
    const targetButton = productForm?.querySelector('[ref="addToCartButton"]') ?? null;
    const footer = this.#resolveFooter();
    const currentStickyButton = this.refs.addToCartButton;

    if (footer) {
      this.#disconnectLateFooterObserver();
    } else {
      this.#observeLateFooterInsertion();
    }

    if (
      !productForm ||
      !buyButtonsBlock ||
      !(targetButton instanceof HTMLButtonElement) ||
      !footer ||
      !(currentStickyButton instanceof HTMLButtonElement)
    ) {
      return this.#handleIncompleteBindings(currentStickyButton);
    }

    const footerContent = footer.querySelector(".footer-content");
    this.#bindLayoutObservers(buyButtonsBlock, footer, footerContent);

    const previousStickyButton = this.#boundStickyButton;
    this.#targetAddToCartButton = targetButton;
    this.#boundStickyButton = currentStickyButton;
    this.#recoverReplacedStickyFocus(previousStickyButton, currentStickyButton);
    this.#observeSourceError();
    this.#setupRetryCount = 0;
    this.#reconcileVisibilityFromGeometry();
    return true;
  }

  /** @param {Element | undefined} currentStickyButton */
  #handleIncompleteBindings(currentStickyButton) {
    this.#observeSourceError();
    this.#hideStickyBar();
    this.#disconnectLayoutObservers();
    this.#targetAddToCartButton = null;
    if (currentStickyButton instanceof HTMLButtonElement) {
      this.#recoverReplacedStickyFocus(this.#boundStickyButton, currentStickyButton);
      this.#boundStickyButton = currentStickyButton;
    }
    this.#scheduleSetupRetry();
    return false;
  }

  /**
   * @param {Element} buyButtonsBlock
   * @param {HTMLElement} footer
   * @param {Element | null} footerContent
   */
  #bindLayoutObservers(buyButtonsBlock, footer, footerContent) {
    const layoutBindingChanged = buyButtonsBlock !== this.#buyButtonsBlock || footer !== this.#footer;
    const footerContentChanged = footerContent !== this.#footerContentEl;
    const previousFooterMargin = this.#footerHideMargin;

    if (layoutBindingChanged) {
      this.#disconnectLayoutObservers();
      this.#buyButtonsBlock = buyButtonsBlock;
      this.#footer = footer;
      this.#footerContentEl = footerContent instanceof HTMLElement ? footerContent : null;
      this.#buyButtonsIntersectionObserver = new IntersectionObserver(this.#onBuyButtonsIntersect);
      this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
      this.#buildFooterObserver();
      this.#observeFooterChanges();
    } else {
      this.#footerContentEl = footerContent instanceof HTMLElement ? footerContent : null;
      const nextFooterMargin = this.#measureFooterHideMargin();
      if (footerContentChanged || nextFooterMargin !== previousFooterMargin || !this.#mainBottomObserver) {
        this.#buildFooterObserver();
      }
      if (!this.#footerMutationObserver) {
        this.#observeFooterChanges();
      }
    }
  }

  /** Watches only this product section for replaced form/buy-button nodes. */
  #observeSectionChanges() {
    this.#sectionMutationObserver?.disconnect();
    const section = this.closest(SHOPIFY_SECTION_SELECTOR);
    if (!section) {
      this.#sectionMutationObserver = null;
      return;
    }

    this.#sectionMutationObserver = new MutationObserver(mutations => {
      const bindingDetached = Boolean(
        (this.#buyButtonsBlock && !this.#buyButtonsBlock.isConnected) ||
        (this.#targetAddToCartButton && !this.#targetAddToCartButton.isConnected),
      );
      if (bindingDetached || this.#mutationsTouchBindings(mutations)) {
        this.#queueSetup({ resetRetry: true });
      }
    });
    this.#sectionMutationObserver.observe(section, { childList: true, subtree: true });
  }

  /** @param {MutationRecord[]} mutations */
  #mutationsTouchBindings(mutations) {
    for (const mutation of mutations) {
      for (const node of [...mutation.addedNodes, ...mutation.removedNodes]) {
        if (
          node instanceof Element &&
          (node.matches(STICKY_BINDING_SELECTOR) || node.querySelector(STICKY_BINDING_SELECTOR))
        ) {
          return true;
        }
      }
    }
    return false;
  }

  /** @returns {HTMLElement | null} */
  #resolveFooter() {
    const footerCandidate =
      document.querySelector(PAGE_FOOTER_SELECTOR) ?? document.querySelector('[class*="footer-group"]');
    return footerCandidate instanceof HTMLElement ? footerCandidate : null;
  }

  /**
   * Watches the document only while the required out-of-section footer is absent.
   * Section mutations cover form/button replacement, but cannot see the first footer
   * arriving after the bounded parse-order retries have ended.
   */
  #observeLateFooterInsertion() {
    if (this.#lateFooterMutationObserver || this.#resolveFooter()) {
      return;
    }

    const observationRoot = document.body ?? document.documentElement;
    if (!observationRoot) {
      return;
    }

    this.#lateFooterMutationObserver = new MutationObserver(mutations => {
      if (!this.isConnected || !this.#mutationsAddFooter(mutations) || !this.#resolveFooter()) {
        return;
      }

      this.#disconnectLateFooterObserver();
      this.#queueSetup();
    });
    this.#lateFooterMutationObserver.observe(observationRoot, { childList: true, subtree: true });
  }

  /** @param {MutationRecord[]} mutations */
  #mutationsAddFooter(mutations) {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (
          node instanceof Element &&
          (node.matches(FOOTER_BINDING_SELECTOR) || node.querySelector(FOOTER_BINDING_SELECTOR))
        ) {
          return true;
        }
      }
    }

    return false;
  }

  /** Stops the temporary document-wide observer as soon as its sole dependency resolves. */
  #disconnectLateFooterObserver() {
    this.#lateFooterMutationObserver?.disconnect();
    this.#lateFooterMutationObserver = null;
  }

  /** Re-runs setup if the current footer or its visible-content wrapper is replaced. */
  #observeFooterChanges() {
    this.#footerMutationObserver?.disconnect();
    const footer = this.#footer;
    if (!footer) {
      this.#footerMutationObserver = null;
      return;
    }

    this.#footerMutationObserver = new MutationObserver(() => this.#queueSetup({ resetRetry: true }));
    this.#footerMutationObserver.observe(footer, { childList: true, subtree: true });
    if (footer.parentElement) {
      this.#footerMutationObserver.observe(footer.parentElement, { childList: true });
    }
  }

  /** Explicitly unobserves outgoing nodes before clearing their identities. */
  #disconnectLayoutObservers() {
    if (this.#buyButtonsIntersectionObserver && this.#buyButtonsBlock) {
      this.#buyButtonsIntersectionObserver.unobserve(this.#buyButtonsBlock);
    }
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#buyButtonsIntersectionObserver = null;

    if (this.#mainBottomObserver && this.#footer) {
      this.#mainBottomObserver.unobserve(this.#footer);
    }
    this.#mainBottomObserver?.disconnect();
    this.#mainBottomObserver = null;
    this.#footerMutationObserver?.disconnect();
    this.#footerMutationObserver = null;
    this.#buyButtonsBlock = null;
    this.#footer = null;
    this.#footerContentEl = null;
    this.#footerHideMargin = 0;
    this.#buyButtonsAbove = false;
    this.#hiddenByBottom = false;
  }

  /**
   * Creates the footer observer. Its negative bottom rootMargin shrinks the root's
   * bottom edge up into the viewport by {@link #footerHideMargin}, so the footer
   * "intersects" right as its first visible content is about to appear — the cue to
   * hide the bar.
   */
  #buildFooterObserver() {
    if (!this.#footer) {
      return;
    }
    if (this.#mainBottomObserver) {
      this.#mainBottomObserver.unobserve(this.#footer);
    }
    this.#mainBottomObserver?.disconnect();
    this.#mainBottomObserver = new IntersectionObserver(this.#onFooterIntersect, {
      rootMargin: `0px 0px -${Math.round(this.#measureFooterHideMargin())}px 0px`,
    });
    this.#mainBottomObserver.observe(this.#footer);
  }

  /**
   * Measures and caches the footer observer's bottom margin: the footer content's top
   * padding (gap from the footer box top to its first visible content) minus a small
   * lead, so the bar hides just before that content scrolls into view. Falls back to 0
   * (hide as the footer box itself enters) when the content wrapper isn't found.
   * @returns {number}
   */
  #measureFooterHideMargin() {
    const el = this.#footerContentEl;
    const padTop = el ? Number.parseFloat(getComputedStyle(el).paddingTop) || 0 : 0;
    this.#footerHideMargin = Math.max(0, padTop - FOOTER_HIDE_LEAD_PX);
    return this.#footerHideMargin;
  }

  // Public action handlers

  /**
   * Holds server-driven relabels while the form-owned transaction is active.
   * @param {"busy" | "success" | "error"} state
   */
  setAddToCartTransactionState(state) {
    this.#addToCartTransactionActive = true;
    this.dataset.addToCartState = state;
  }

  /**
   * Releases the shared transaction and applies the latest deferred variant
   * update only after the captured purchase action has fully settled.
   */
  settleAddToCartTransaction() {
    this.#addToCartTransactionActive = false;
    delete this.dataset.addToCartState;

    const pendingVariantUpdate = this.#pendingVariantUpdate;
    this.#pendingVariantUpdate = null;
    if (pendingVariantUpdate) {
      this.#applyVariantUpdate(pendingVariantUpdate);
    }
  }

  /** Reads both observed boundaries after setup/viewport/history changes. */
  #reconcileVisibilityFromGeometry() {
    if (!this.#buyButtonsBlock || !this.#footer) {
      this.#hideStickyBar();
      return;
    }

    this.#syncBuyButtonsStateFromGeometry();
    this.#syncFooterStateFromGeometry();
    this.#updateStickyVisibility();
  }

  /** Keeps the active-zone state current outside IntersectionObserver delivery. */
  #syncBuyButtonsStateFromGeometry() {
    const buyButtonsBlock = this.#buyButtonsBlock;
    if (!buyButtonsBlock) {
      this.#buyButtonsAbove = false;
      return;
    }

    const rect = buyButtonsBlock.getBoundingClientRect();
    this.#buyButtonsAbove = rect.bottom <= 0;
  }

  /** Matches the footer observer's shrunken bottom boundary using live viewport geometry. */
  #syncFooterStateFromGeometry() {
    const footer = this.#footer;
    if (!footer) {
      this.#hiddenByBottom = false;
      return;
    }

    const viewportBottom = this.#getViewportHeight() - this.#measureFooterHideMargin();
    this.#hiddenByBottom = footer.getBoundingClientRect().top <= viewportBottom;
  }

  /** @returns {number} */
  #getViewportHeight() {
    return Number(globalThis.visualViewport?.height) || globalThis.innerHeight;
  }

  /** Applies one visibility predicate for observer, retry, resize, and history paths. */
  #updateStickyVisibility() {
    const shouldShow =
      this.dataset.variantAvailable === TRUE &&
      this.#buyButtonsAbove &&
      !this.#hiddenByBottom &&
      !this.#isVirtualKeyboardLikelyOpen() &&
      this.#hasRoomToEngage();

    if (shouldShow) {
      this.#showStickyBar();
    } else {
      this.#hideStickyBar();
    }
  }

  /** Avoids obscuring focused fields when a mobile visual viewport is keyboard-shrunk. */
  #isVirtualKeyboardLikelyOpen() {
    const visualViewportHeight = Number(globalThis.visualViewport?.height);
    return Boolean(
      visualViewportHeight && globalThis.innerHeight - visualViewportHeight >= VIRTUAL_KEYBOARD_MIN_DELTA_PX,
    );
  }

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    if (this.dataset.variantAvailable !== TRUE || this.#hiddenByBottom || !this.#hasRoomToEngage()) {
      this.#hideStickyBar();
      return;
    }

    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = TRUE;
    // Make the bar interactive again for keyboard/AT (it's `inert` while hidden so its
    // duplicate add-to-cart button isn't tabbable/announced off-screen).
    stickyBar.inert = false;
    // Reflect any error currently on screen in the source so the banner stays in
    // sync with the buy buttons whenever the bar becomes visible.
    this.#syncErrorFromSource();
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#moveFocusBeforeHide();
    this.#isStuck = false;
    stickyBar.dataset.stuck = FALSE;
    // Pull the hidden bar out of the keyboard/AT tab order — it's still display:block
    // (translated off-screen) for the slide-out, and pointer-events:none only covers
    // the pointer; `inert` covers keyboard + screen readers.
    stickyBar.inert = true;
    // Hide the banner and restore the original error in the buy buttons (now back
    // in view), keeping the error in exactly one place.
    this.#syncErrorFromSource();
  }

  /** Moves focus before applying inert so no hidden sticky control strands the user. */
  #moveFocusBeforeHide() {
    const stickyBar = this.refs.stickyBar;
    const activeElement = document.activeElement;
    const lostStickyFocus = Boolean(
      this.#lastFocusedStickyControl &&
      stickyBar.contains(this.#lastFocusedStickyControl) &&
      activeElement === document.body,
    );
    if (!stickyBar.contains(activeElement) && !lostStickyFocus) {
      return;
    }

    this.#focusSafeFallback();
  }

  /**
   * Restores a logical target after a morph replaces the focused sticky button.
   * @param {HTMLButtonElement | null} previousButton
   * @param {HTMLButtonElement} currentButton
   */
  #recoverReplacedStickyFocus(previousButton, currentButton) {
    if (
      !previousButton ||
      previousButton === currentButton ||
      previousButton.isConnected ||
      this.#lastFocusedStickyControl !== previousButton ||
      document.activeElement !== document.body
    ) {
      return;
    }

    if (
      this.#isStuck &&
      this.dataset.variantAvailable === TRUE &&
      !currentButton.disabled &&
      !this.refs.stickyBar.inert
    ) {
      currentButton.focus({ preventScroll: true });
      this.#lastFocusedStickyControl = currentButton;
      return;
    }

    this.#focusSafeFallback();
  }

  /** Recovers focus when the whole sticky component is detached while focused. */
  #recoverFocusForDetachedStickyControl() {
    if (
      !this.#lastFocusedStickyControl ||
      this.#lastFocusedStickyControl.isConnected ||
      document.activeElement !== document.body
    ) {
      return;
    }

    const primaryButton = this.#targetAddToCartButton;
    if (primaryButton?.isConnected && !primaryButton.disabled) {
      primaryButton.focus();
      if (document.activeElement === primaryButton) {
        this.#lastFocusedStickyControl = null;
        return;
      }
    }

    this.#focusSafeFallback();
  }

  /** Focuses the in-view equivalent, footer content, or next safe section control. */
  #focusSafeFallback() {
    const primaryButton = this.#targetAddToCartButton;
    const footerControl = this.#footer?.querySelector(FOCUSABLE_SELECTOR);
    const section = this.closest(SHOPIFY_SECTION_SELECTOR);
    const sectionControl = section
      ? [...section.querySelectorAll(FOCUSABLE_SELECTOR)].find(element => !this.contains(element))
      : null;
    const candidates = this.#buyButtonsAbove
      ? [footerControl, primaryButton, sectionControl]
      : [primaryButton, footerControl, sectionControl];

    for (const candidate of candidates) {
      const isConnectedElement = candidate instanceof HTMLElement && candidate.isConnected;
      const isHidden = isConnectedElement && candidate.closest("[hidden], [inert], [aria-hidden='true']");
      const isDisabledButton = candidate instanceof HTMLButtonElement && candidate.disabled;

      if (isConnectedElement && !isHidden && !isDisabledButton) {
        candidate.focus();
        if (document.activeElement === candidate) {
          this.#lastFocusedStickyControl = null;
          return;
        }
      }
    }
  }

  /**
   * Whether the bar would stay visible long enough to be worth showing. Prevents the
   * show/hide flash on short pages where the footer is reached almost as soon as the
   * buy buttons leave the viewport — there, it simply never appears.
   * @returns {boolean}
   */
  #hasRoomToEngage() {
    const buyButtonsBlock = this.#buyButtonsBlock;
    const footer = this.#footer;
    if (!buyButtonsBlock || !footer) {
      // Can't measure — fail open rather than suppress.
      return true;
    }

    const { scrollY, innerWidth } = globalThis;
    const buyButtonsBottom = buyButtonsBlock.getBoundingClientRect().bottom + scrollY;
    const footerTop = footer.getBoundingClientRect().top + scrollY;
    // The bar shows once the buy buttons leave the top and hides once the footer has
    // scrolled in by its hide margin, so it's visible across this many px of scroll:
    const visibleScroll = footerTop - buyButtonsBottom - this.#getViewportHeight() + this.#measureFooterHideMargin();
    // Desktop pages are shorter and scroll slower (less flash-prone) than mobile flicks,
    // so they can use a smaller minimum window.
    const minVisible = innerWidth >= DESKTOP_MIN_WIDTH ? MIN_VISIBLE_SCROLL_DESKTOP_PX : MIN_VISIBLE_SCROLL_MOBILE_PX;
    return visibleScroll >= minVisible;
  }

  /**
   * Watches the product form's add-to-cart error element so any error shown there
   * (limit reached, inventory cap, partial add — across all of the product form's
   * error paths) is mirrored into the sticky bar, where a shopper who added from
   * the sticky bar can actually see it. The product form remains the single source
   * of truth for the message text.
   */
  #observeSourceError() {
    const productForm = this.#getProductForm();
    const sourceError = productForm?.querySelector(`[ref="${ADD_TO_CART_ERROR_REF}"]`) ?? null;

    // Only (re)wire the observer when the source node actually changed.
    if (sourceError !== this.#sourceErrorElement) {
      // Un-suppress the outgoing node so a replaced element isn't left hidden.
      this.#setSourceErrorSuppressed(false);
      this.#errorObserver?.disconnect();
      this.#errorObserver = null;
      this.#sourceErrorElement = sourceError instanceof HTMLElement ? sourceError : null;

      if (this.#sourceErrorElement) {
        this.#errorObserver = new MutationObserver(() => this.#syncErrorFromSource());
        this.#errorObserver.observe(this.#sourceErrorElement, {
          attributes: true,
          attributeFilter: ["class"],
          childList: true,
          characterData: true,
          subtree: true,
        });
      }
    }

    // Always reflect the source's current state. This matters when the bar was
    // re-rendered (e.g. a variant update morphs the banner back to its hidden
    // default) while the source error is still on screen and the node is unchanged.
    this.#syncErrorFromSource();
  }

  /**
   * Reflects the source error element's current visibility and message into the
   * sticky bar's error banner.
   */
  #syncErrorFromSource() {
    const source = this.#sourceErrorElement;
    const visible = Boolean(source && !source.classList.contains(HIDDEN_CLASS));
    const message = source?.textContent?.trim() ?? "";

    this.#setStickyError(visible ? message : "");
  }

  /**
   * Shows or clears the sticky bar's error banner. Gated on the bar being stuck
   * (visible) so the invariant is: banner shown ⟺ source error visible AND bar
   * stuck. Recomputed on source mutations, on show, and on hide.
   * @param {string} message - The error message, or an empty string to hide.
   */
  #setStickyError(message) {
    const { stickyError, stickyErrorText } = this.refs;
    if (!stickyError) {
      return;
    }

    const showInBanner = Boolean(message) && this.#isStuck;

    if (showInBanner) {
      if (stickyErrorText) {
        stickyErrorText.textContent = message;
      }
      stickyError.classList.remove(HIDDEN_CLASS);
    } else {
      stickyError.classList.add(HIDDEN_CLASS);
    }

    // Suppress the original error (up in the buy buttons) only when the popup is the
    // one showing it: buy buttons scrolled past AND the page is tall enough for the bar
    // to engage. Gate on #buyButtonsAbove + #hasRoomToEngage() (both STABLE / scroll-
    // invariant), NOT showInBanner/isStuck — the latter oscillates at the footer-hide
    // threshold and would bounce the footer observer (page jitter). The #hasRoomToEngage
    // term keeps the source visible on short pages where the bar never engages, so the
    // error is never hidden in both places at once.
    this.#setSourceErrorSuppressed(Boolean(message) && this.#buyButtonsAbove && this.#hasRoomToEngage());
  }

  /**
   * Hides or restores the product form's original error element while the sticky banner
   * mirrors it. Uses inline `display:none` so the suppressed source takes NO layout
   * space (otherwise the error pushes the page down even though it only shows in the
   * popup). Suppression is gated on #buyButtonsAbove (stable) — see {@link #setStickyError}
   * — so this display toggle fires once when you cross the buy buttons, never at the
   * footer-hide threshold where it would bounce the footer observer (page jitter). The
   * style change is invisible to the class-filtered source observer (no feedback loop),
   * and `.hidden` (`display: none !important`) still wins when the error is cleared.
   * @param {boolean} suppressed - Whether to hide the original error element.
   */
  #setSourceErrorSuppressed(suppressed) {
    const source = this.#sourceErrorElement;
    if (!source) {
      return;
    }

    if (suppressed) {
      source.style.display = "none";
    } else {
      source.style.removeProperty("display");
    }
  }

  // Helper methods
  /**
   * Gets the product form element
   * @returns {ProductFormElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) {
      return null;
    }

    const sectionElement = this.closest(SHOPIFY_SECTION_SELECTOR);
    if (!sectionElement) {
      return null;
    }

    return /** @type {ProductFormElement | null} */ (
      sectionElement.querySelector(`product-form-component[data-product-id="${productId}"]`)
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = Number.parseInt(this.dataset.initialQuantity || "1", 10) || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { quantityDisplay, quantityNumber } = this.refs;

    const available = this.dataset.variantAvailable === TRUE;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    quantityDisplay.style.display = available && this.#currentQuantity > 1 ? "inline" : "none";
  }
}

if (!customElements.get(STICKY_ADD_TO_CART)) {
  customElements.define(STICKY_ADD_TO_CART, StickyAddToCartComponent);
}
